import { useState } from "react";
import { toast } from "sonner";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError, getUTM } from "@/lib/api";

const BUDGETS = ["< $1,000", "$1,000 - $5,000", "$5,000 - $15,000", "$15,000 - $50,000", "$50,000+"];

export default function LeadForm({
  type = "contact",
  services = [],
  showService = true,
  showBudget = true,
  showDate = false,
  submitLabel = "Send message",
}) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", service: "", budget: "", preferred_date: "", message: "" });
  const [consent, setConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) { toast.error("Please accept the privacy consent to continue."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...form, type, consent, source: "website", utm: getUTM() });
      setDone(true);
      toast.success(data.message || "Thank you! We'll be in touch.");
      if (window.posthog) window.posthog.capture(`${type}_submit`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="flex flex-col items-center rounded-2xl border border-border bg-card p-10 text-center" data-testid="lead-form-success">
        <div className="mb-4 rounded-full bg-success/15 p-3 text-success"><CheckCircle2 className="h-8 w-8" /></div>
        <h3 className="font-display text-xl font-bold">Request received</h3>
        <p className="mt-2 max-w-sm text-sm text-muted-foreground">
          Thanks, {form.name.split(" ")[0] || "there"}. A Buddy Solution specialist will get back to you shortly.
          We've also sent a confirmation to your email.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} className="space-y-4" data-testid={`lead-form-${type}`}>
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <Label htmlFor="lf-name">Full name</Label>
          <Input id="lf-name" required value={form.name} onChange={upd("name")} placeholder="Jane Doe" className="mt-1.5" data-testid="lead-name" />
        </div>
        <div>
          <Label htmlFor="lf-email">Email</Label>
          <Input id="lf-email" type="email" required value={form.email} onChange={upd("email")} placeholder="jane@company.com" className="mt-1.5" data-testid="lead-email" />
        </div>
        <div>
          <Label htmlFor="lf-phone">Phone (optional)</Label>
          <Input id="lf-phone" value={form.phone} onChange={upd("phone")} placeholder="+1 555 000 0000" className="mt-1.5" data-testid="lead-phone" />
        </div>
        <div>
          <Label htmlFor="lf-company">Company (optional)</Label>
          <Input id="lf-company" value={form.company} onChange={upd("company")} placeholder="Acme Inc." className="mt-1.5" data-testid="lead-company" />
        </div>
        {showService && (
          <div>
            <Label>Service of interest</Label>
            <Select value={form.service} onValueChange={upd("service")}>
              <SelectTrigger className="mt-1.5" data-testid="lead-service"><SelectValue placeholder="Choose a service" /></SelectTrigger>
              <SelectContent>
                {services.map((s) => <SelectItem key={s.slug || s} value={s.title || s}>{s.title || s}</SelectItem>)}
                <SelectItem value="Not sure yet">Not sure yet</SelectItem>
              </SelectContent>
            </Select>
          </div>
        )}
        {showBudget && (
          <div>
            <Label>Estimated budget</Label>
            <Select value={form.budget} onValueChange={upd("budget")}>
              <SelectTrigger className="mt-1.5" data-testid="lead-budget"><SelectValue placeholder="Select a range" /></SelectTrigger>
              <SelectContent>
                {BUDGETS.map((b) => <SelectItem key={b} value={b}>{b}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        )}
        {showDate && (
          <div className="sm:col-span-2">
            <Label htmlFor="lf-date">Preferred date/time</Label>
            <Input id="lf-date" type="datetime-local" value={form.preferred_date} onChange={upd("preferred_date")} className="mt-1.5" data-testid="lead-date" />
          </div>
        )}
      </div>
      <div>
        <Label htmlFor="lf-message">Project details</Label>
        <Textarea id="lf-message" rows={4} value={form.message} onChange={upd("message")} placeholder="Tell us about your goals, timeline and scope..." className="mt-1.5" data-testid="lead-message" />
      </div>
      <div className="flex items-start gap-3">
        <Checkbox id="lf-consent" checked={consent} onCheckedChange={setConsent} data-testid="lead-consent" className="mt-0.5" />
        <Label htmlFor="lf-consent" className="text-sm font-normal leading-relaxed text-muted-foreground">
          I consent to Buddy Solution storing and processing my details to respond to this enquiry, in line with the Privacy Policy.
        </Label>
      </div>
      <Button type="submit" size="lg" disabled={busy} className="w-full rounded-full sm:w-auto" data-testid="lead-submit">
        {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} {submitLabel}
      </Button>
    </form>
  );
}
